// A control for any value, built from its JSON Schema — the part of the renderer that means a new
// setting needs no new screen (spec/03 §9, spec/13 §13).
//
// Every control is one of the component kit's (packages/ui-web), so labels, descriptions, errors and
// their ARIA wiring come from `Field` and nothing here re-implements them. Objects and lists are
// fieldsets with a legend, so a screen reader hears which question a nested control belongs to.

import { countries } from '@clavian/i18n';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Field, Input, RadioGroup, SelectField, TextField } from '@clavian/ui-web';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { jsonEqual } from '../lib/json';

import { HoursRowControl, TimeRangesControl, WeekControl } from './hours';
import { parseMajorAmount, type SettingMoney } from './money';
import { isScalarList, ScalarChipsControl } from './special-controls';
import {
  controlKind,
  CUSTOM_CONTROL,
  discriminatorOf,
  emptyValue,
  enumOf,
  fieldWidth,
  propertiesOf,
  requiredOf,
  resolveSchema,
  variantsOf,
  type JsonSchema,
} from './schema';
import { errorKey, type ControlProps } from './types';

/** Radios read better than a menu for a short list of long labels. */
const MAX_RADIO_OPTIONS = 4;

const namesOnly = (path: readonly (string | number)[]): string[] =>
  path.filter((segment): segment is string => typeof segment === 'string');

/** A label inside a sentence ("Add hours", "Remove map pin"): a field's name, not a title. */
const inSentence = (label: string): string => label.toLocaleLowerCase();

export function SchemaControl(props: ControlProps): ReactNode {
  const kind = controlKind(props.schema, props.context);
  switch (kind) {
    case 'custom': {
      const Custom = props.custom[String(props.schema[CUSTOM_CONTROL])];
      return Custom === undefined ? <JsonControl {...props} /> : <Custom {...props} />;
    }
    case 'constant':
      return null;
    case 'choice':
      return <ChoiceControl {...props} />;
    case 'checkbox':
      return <CheckboxControl {...props} />;
    case 'multi-choice':
      return <MultiChoiceControl {...props} />;
    case 'country':
      return <CountryControl {...props} />;
    case 'time-ranges':
      return <TimeRangesControl {...props} />;
    case 'week':
      return <WeekControl {...props} />;
    case 'hours-row':
      return <HoursRowControl {...props} renderFields={(names) => <FieldsGrid control={props} names={names} />} />;
    case 'object':
      return <ObjectControl {...props} />;
    case 'list':
      return <ListControl {...props} />;
    case 'variant':
      return <VariantControl {...props} />;
    case 'json':
      return <JsonControl {...props} />;
    case 'money':
      return props.context.money === undefined ? <TextControl {...props} kind="number" /> : <MoneyControl {...props} money={props.context.money} />;
    default:
      return <TextControl {...props} kind={kind} />;
  }
}

// ---------------------------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------------------------

const INPUT_TYPES = { text: 'text', email: 'email', url: 'url', time: 'time', date: 'date', number: 'number' } as const;

/**
 * How wide a text box is when it stands alone in a setting's row: a time or a number is short, a
 * slug or an email medium, anything else capped rather than stretched across the card. Inside an
 * object the grid decides (`FieldsGrid`).
 */
function inputSize(kind: string, schema: JsonSchema): 'narrow' | 'medium' | 'wide' {
  if (kind === 'time' || kind === 'date' || kind === 'number') return 'narrow';
  const maxLength = schema['maxLength'];
  if (kind === 'email' || (typeof maxLength === 'number' && maxLength <= 64)) return 'medium';
  return 'wide';
}

function TextControl(props: ControlProps & { readonly kind: keyof typeof INPUT_TYPES | string }) {
  const { schema, value, onChange, required, nullable } = props;
  const numeric = props.kind === 'number';
  const type = INPUT_TYPES[props.kind as keyof typeof INPUT_TYPES] ?? 'text';
  const text = value === undefined || value === null ? '' : String(value);

  const change = (raw: string) => {
    if (raw === '') {
      // An empty optional field is an absent one on the wire, not an empty string.
      onChange(required ? (numeric ? undefined : '') : nullable ? null : undefined);
      return;
    }
    if (!numeric) {
      onChange(raw);
      return;
    }
    const number = Number(raw);
    onChange(Number.isFinite(number) ? number : raw);
  };

  return (
    <TextField
      className={`console-input--${inputSize(props.kind, schema)}`}
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      required={required}
      showOptional={!required}
      type={type}
      numeric={numeric}
      value={text}
      disabled={props.disabled}
      onChange={(event) => change(event.target.value)}
      {...(typeof schema['maxLength'] === 'number' ? { maxLength: schema['maxLength'] } : {})}
      {...(numeric && typeof schema['minimum'] === 'number' ? { min: schema['minimum'] } : {})}
      {...(numeric && typeof schema['maximum'] === 'number' ? { max: schema['maximum'] } : {})}
      {...(numeric ? { step: schema['type'] === 'integer' ? 1 : 'any' } : {})}
      {...(type === 'text' && typeof schema['pattern'] === 'string' && schema['pattern'].startsWith('^\\+')
        ? { inputMode: 'tel' as const, autoComplete: 'tel' }
        : {})}
    />
  );
}

/**
 * An amount of money (`x-clavian-unit: money`, spec/03 §9): the currency's symbol beside a field that
 * takes the major unit — "20000", "20,000", "99.5" — and hands on integer minor units (spec/06 §2).
 * While the text is not an amount the field says why and the form will not save (`onUnparsed`); when
 * it is, the text is left as typed until the field loses focus, then shown grouped ("20,000").
 */
function MoneyControl(props: ControlProps & { readonly money: SettingMoney }) {
  const { t } = useTranslation('console');
  const { money, value, onChange, required, nullable } = props;
  const show = (next: unknown) => (typeof next === 'number' && Number.isSafeInteger(next) ? money.formatInput(next) : '');
  const [text, setText] = useState(() => show(value));
  const [problem, setProblem] = useState<'invalid' | 'fraction' | 'whole' | undefined>(undefined);
  // The value this field last handed on. When the value changes from outside — Discard, a reset, the
  // fresh value after a 412 — the text follows it.
  const [emitted, setEmitted] = useState<unknown>(value);
  if (!jsonEqual(value, emitted)) {
    setEmitted(value);
    setText(show(value));
    setProblem(undefined);
  }
  const key = errorKey(props.entryKey, props.path);
  const { onUnparsed } = props;
  useEffect(() => {
    if (problem === undefined) return undefined;
    onUnparsed?.(key, true);
    return () => onUnparsed?.(key, false);
  }, [onUnparsed, key, problem]);

  const minimum = props.schema['minimum'];
  const negative = !(typeof minimum === 'number' && minimum >= 0);
  const emit = (next: unknown) => {
    setEmitted(next);
    onChange(next);
  };
  const change = (raw: string) => {
    setText(raw);
    if (raw.trim() === '') {
      setProblem(undefined);
      emit(required ? undefined : nullable ? null : undefined);
      return;
    }
    const parsed = parseMajorAmount(raw, money, negative);
    if (!parsed.ok) {
      setProblem(parsed.reason);
      return;
    }
    setProblem(undefined);
    emit(parsed.minor);
  };

  const error =
    problem === 'fraction'
      ? t('settings.control.moneyFraction', { count: money.digits })
      : problem === 'whole'
        ? t('settings.control.moneyWhole')
        : problem === 'invalid'
          ? t('settings.control.moneyInvalid')
          : props.errorAt(props.path);
  return (
    <Field
      className="console-input--narrow"
      label={props.label}
      description={props.description}
      error={error}
      required={required}
      showOptional={!required}
    >
      {(control) => (
        <div className="console-money-field">
          <span className="console-money-field__symbol" aria-hidden="true">
            {money.symbol}
          </span>
          <Input
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            required={control.required}
            numeric
            inputMode="decimal"
            autoComplete="off"
            value={text}
            disabled={props.disabled}
            data-currency={money.currency}
            onChange={(event) => change(event.target.value)}
            onBlur={() => {
              if (problem === undefined && typeof emitted === 'number') setText(show(emitted));
            }}
          />
        </div>
      )}
    </Field>
  );
}

function ChoiceControl(props: ControlProps) {
  const choices = enumOf(props.schema) ?? [];
  const options = choices.map((choice) => ({
    value: String(choice),
    label: props.labels.option(props.entryKey, namesOnly(props.path), choice),
  }));
  const selected = props.value === undefined || props.value === null ? undefined : String(props.value);
  const choose = (next: string) => onChoose(props, choices, next);
  const error = props.errorAt(props.path);

  if (options.length <= MAX_RADIO_OPTIONS) {
    return (
      <RadioGroup
        label={props.label}
        description={props.description}
        error={error}
        options={options}
        disabled={props.disabled}
        required={props.required}
        {...(selected === undefined ? {} : { value: selected })}
        onValueChange={choose}
      />
    );
  }
  return (
    <SelectField
      label={props.label}
      description={props.description}
      error={error}
      options={options}
      disabled={props.disabled}
      required={props.required}
      {...(selected === undefined ? {} : { value: selected })}
      onValueChange={choose}
    />
  );
}

function onChoose(props: ControlProps, choices: readonly (string | number | boolean)[], next: string): void {
  props.onChange(choices.find((choice) => String(choice) === next) ?? next);
}

function CheckboxControl(props: ControlProps) {
  const id = useId();
  const error = props.errorAt(props.path);
  const describedBy = [props.description ? `${id}-description` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="console-control">
      <Checkbox
        label={props.label}
        checked={props.value === true}
        disabled={props.disabled}
        onCheckedChange={(checked) => props.onChange(checked === true)}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        {...(error ? { 'aria-invalid': true } : {})}
      />
      {props.description ? (
        <p className="clav-field__description" id={`${id}-description`}>
          {props.description}
        </p>
      ) : null}
      {error ? (
        <p className="clav-field__error" id={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function MultiChoiceControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const items = props.schema['items'] as JsonSchema;
  const choices = enumOf(resolveSchema(items, props.context).schema) ?? [];
  const selected = Array.isArray(props.value) ? props.value : [];
  const toggle = (choice: string | number | boolean, on: boolean) => {
    const next = on ? [...selected, choice] : selected.filter((item) => item !== choice);
    // Kept in the schema's order, so the same set always reads and saves the same way.
    props.onChange(choices.filter((option) => next.includes(option)));
  };
  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-choices">
          {choices.length === 0 ? <p>{t('settings.control.noOptions')}</p> : null}
          {choices.map((choice) => (
            <Checkbox
              key={String(choice)}
              label={props.labels.option(props.entryKey, namesOnly(props.path), choice)}
              checked={selected.includes(choice)}
              disabled={props.disabled}
              onCheckedChange={(checked) => toggle(choice, checked === true)}
            />
          ))}
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------
// Objects, lists and unions
// ---------------------------------------------------------------------------------------------

function ObjectControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const properties = propertiesOf(props.schema);
  const value = props.value !== null && typeof props.value === 'object' ? (props.value as Record<string, unknown>) : null;

  if (value === null && props.nullable) {
    return (
      <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
        {() => (
          <div className="console-nested">
            <p className="console-muted">{t('settings.control.notSet')}</p>
            {props.disabled ? null : (
              <Button size="sm" onClick={() => props.onChange(emptyValue(props.schema, props.context))}>
                {t('settings.control.add', { item: inSentence(props.label) })}
              </Button>
            )}
          </div>
        )}
      </Field>
    );
  }

  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-nested">
          <FieldsGrid control={props} names={Object.keys(properties)} />
          {props.nullable && !props.disabled ? (
            <div>
              <Button size="sm" variant="ghost" onClick={() => props.onChange(null)}>
                {t('settings.control.clear', { item: inSentence(props.label) })}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}

/**
 * An object's fields, laid out by width: short ones — a city, a state, a PIN code; a latitude and a
 * longitude — share a row on a wide screen, and every field stacks on a narrow one.
 */
function FieldsGrid({ control: props, names }: { readonly control: ControlProps; readonly names: readonly string[] }) {
  const properties = propertiesOf(props.schema);
  const required = requiredOf(props.schema);
  const current = props.value !== null && typeof props.value === 'object' ? (props.value as Record<string, unknown>) : {};
  return (
    <div className="console-fields">
      {names.map((name) => {
        const property = properties[name];
        if (property === undefined) return null;
        const resolved = resolveSchema(property, props.context);
        const path = [...props.path, name];
        const labelPath = namesOnly(path);
        return (
          <div key={name} className={`console-field console-field--${fieldWidth(resolved.schema, props.context)}`}>
            <SchemaControl
              {...props}
              schema={resolved.schema}
              nullable={resolved.nullable}
              path={path}
              label={props.labels.field(props.entryKey, labelPath)}
              description={props.labels.description(props.entryKey, labelPath, resolved.schema)}
              value={current[name]}
              required={required.has(name)}
              onChange={(next) => {
                const updated = { ...current };
                if (next === undefined) delete updated[name];
                else updated[name] = next;
                props.onChange(updated);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * A country: chosen by name, in the reader's language, and stored as its ISO code (the contract's
 * `CountryCode`). A code the list does not know is kept and shown as itself, never dropped.
 */
function CountryControl(props: ControlProps) {
  const { i18n } = useTranslation('console');
  const value = typeof props.value === 'string' && props.value !== '' ? props.value : undefined;
  const options = useMemo(() => {
    const known = countries(i18n.language).map((country) => ({ value: country.code, label: country.name }));
    return value === undefined || known.some((option) => option.value === value) ? known : [{ value, label: value }, ...known];
  }, [i18n.language, value]);
  return (
    <SelectField
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      options={options}
      disabled={props.disabled}
      required={props.required}
      {...(value === undefined ? {} : { value })}
      onValueChange={props.onChange}
    />
  );
}

function ListControl(props: ControlProps) {
  // A list of plain values reads as chips with an inline add, never "Item 1 / Add item" rows.
  if (isScalarList(props.schema, props)) return <ScalarChipsControl {...props} />;
  return <ShapeListControl {...props} />;
}

function ShapeListControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const items = resolveSchema(props.schema['items'] as JsonSchema, props.context);
  const list = Array.isArray(props.value) ? props.value : [];
  const maxItems = typeof props.schema['maxItems'] === 'number' ? props.schema['maxItems'] : Infinity;
  const itemLabel = props.labels.field(props.entryKey, [...namesOnly(props.path), '$item']);

  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-list">
          {list.length === 0 ? <p className="console-muted">{t('settings.control.empty')}</p> : null}
          {list.map((item, index) => {
            const label = `${itemLabel} ${index + 1}`;
            return (
              // Keyed by position: the rows have no identity of their own, and a value's order is
              // part of the value.
              <div className="console-list__item" key={index}>
                <SchemaControl
                  {...props}
                  schema={items.schema}
                  nullable={items.nullable}
                  path={[...props.path, index]}
                  label={label}
                  description={undefined}
                  value={item}
                  required
                  onChange={(next) => props.onChange(list.map((existing, at) => (at === index ? next : existing)))}
                />
                {props.disabled ? null : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => props.onChange(list.filter((_, at) => at !== index))}
                  >
                    {t('settings.control.remove', { item: inSentence(label) })}
                  </Button>
                )}
              </div>
            );
          })}
          {props.disabled ? null : (
            <div>
              <Button
                size="sm"
                disabled={list.length >= maxItems}
                onClick={() => props.onChange([...list, emptyValue(items.schema, props.context)])}
              >
                {t('settings.control.add', { item: inSentence(itemLabel) })}
              </Button>
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

function VariantControl(props: ControlProps) {
  const variants = variantsOf(props.schema, props.context);
  const tag = discriminatorOf(variants) as string;
  const current = props.value !== null && typeof props.value === 'object' ? (props.value as Record<string, unknown>) : {};
  const chosen = variants.find((variant) => propertiesOf(variant)[tag]?.['const'] === current[tag]) ?? variants[0];
  const tagValues = variants.map((variant) => propertiesOf(variant)[tag]?.['const'] as string | number | boolean);
  const tagPath = [...props.path, tag];

  const choose = (next: string | number | boolean) => {
    const variant = variants.find((candidate) => propertiesOf(candidate)[tag]?.['const'] === next);
    if (variant === undefined) return;
    props.onChange({ ...(emptyValue(variant, props.context) as Record<string, unknown>), [tag]: next });
  };

  const rest: JsonSchema = chosen === undefined ? {} : { ...chosen, properties: withoutKey(propertiesOf(chosen), tag) };

  return (
    <div className="console-variant">
      <SchemaControl
        {...props}
        schema={{ enum: tagValues }}
        nullable={false}
        path={tagPath}
        value={current[tag]}
        required
        onChange={(next) => choose(next as string | number | boolean)}
      />
      {Object.keys(propertiesOf(rest)).length > 0 ? (
        <ObjectControl
          {...props}
          schema={rest}
          nullable={false}
          label={props.labels.field(props.entryKey, [...namesOnly(props.path), String(current[tag] ?? '')])}
          description={undefined}
          value={current}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The fallback: a value no control above understands is edited as JSON rather than cut down to
// the part a control does understand.
// ---------------------------------------------------------------------------------------------

function JsonControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const show = (value: unknown) => JSON.stringify(value ?? null, null, 2);
  const [text, setText] = useState(() => show(props.value));
  const [invalid, setInvalid] = useState(false);
  // The value this editor last handed on. When the value changes from outside — Discard, a reset,
  // the fresh value after a 412 — the text follows it rather than keeping the old document.
  const [emitted, setEmitted] = useState<unknown>(props.value);
  if (!jsonEqual(props.value, emitted)) {
    setEmitted(props.value);
    setText(show(props.value));
    setInvalid(false);
  }
  const key = errorKey(props.entryKey, props.path);
  const { onUnparsed } = props;
  useEffect(() => {
    if (!invalid) return undefined;
    onUnparsed?.(key, true);
    return () => onUnparsed?.(key, false);
  }, [onUnparsed, key, invalid]);
  const error = invalid ? t('settings.control.jsonInvalid') : props.errorAt(props.path);
  return (
    <Field label={props.label} description={props.description ?? t('settings.control.jsonHint')} error={error}>
      {(control) => (
        <textarea
          id={control.id}
          aria-describedby={control['aria-describedby']}
          aria-invalid={control['aria-invalid']}
          className="clav-input console-json"
          rows={Math.min(12, text.split('\n').length + 1)}
          spellCheck={false}
          disabled={props.disabled}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            try {
              const parsed = JSON.parse(event.target.value) as unknown;
              setEmitted(parsed);
              props.onChange(parsed);
              setInvalid(false);
            } catch {
              setInvalid(true);
            }
          }}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------

function withoutKey(record: Readonly<Record<string, JsonSchema>>, key: string): Record<string, JsonSchema> {
  const { [key]: _ignored, ...rest } = record;
  return rest;
}
